import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { CheckCircle2, RefreshCw } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const DEFAULT_ITEMS = [
  'Lobi dan koridor disapu & dipel',
  'Kamar mandi bersama dibersihkan',
  'Tempat sampah dikosongkan',
  'Area parkir dirapikan',
  'Dapur bersama dibersihkan',
  'Dispenser & area pantry dicek',
  'Jendela & ventilasi dibersihkan',
  'Pengecekan stok sabun/kertas'
];

const buildDefaultItems = () => DEFAULT_ITEMS.map((label) => ({ label, done: false }));

const formatDateInput = (date) => {
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const day = `${date.getDate()}`.padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const shiftDate = (dateStr, days) => {
  const base = dateStr ? new Date(`${dateStr}T00:00:00`) : new Date();
  base.setDate(base.getDate() + days);
  return formatDateInput(base);
};

const HousekeepingChecklist = ({ userRole }) => {
  const [properties, setProperties] = useState([]);
  const [selectedProperty, setSelectedProperty] = useState('');
  const [selectedDate, setSelectedDate] = useState(formatDateInput(new Date()));
  const [items, setItems] = useState(buildDefaultItems());
  const [notes, setNotes] = useState('');
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const canEdit = ['housekeeping', 'admin', 'superadmin'].includes(userRole);

  const completion = useMemo(() => {
    const doneCount = items.filter((item) => item.done).length;
    const total = items.length || 1;
    return {
      doneCount,
      total,
      percent: Math.round((doneCount / total) * 100)
    };
  }, [items]);

  const fetchProperties = async () => {
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const res = await axios.get(`${BACKEND_URL}/api/staff/properties`, config);
      setProperties(res.data);
      if (res.data.length > 0 && !selectedProperty) {
        setSelectedProperty(res.data[0].name);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Gagal memuat properti');
    } finally {
      setLoading(false);
    }
  };

  const normalizeItems = (itemsData) => {
    if (!itemsData || itemsData.length === 0) return buildDefaultItems();
    return itemsData.map((item) => ({
      label: item.label || '',
      done: Boolean(item.done)
    }));
  };

  const fetchHistory = async () => {
    if (!selectedProperty) return;
    try {
      setRefreshing(true);
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const dateFrom = shiftDate(selectedDate, -13);
      const url = `${BACKEND_URL}/api/staff/housekeeping/checklists?property_name=${encodeURIComponent(selectedProperty)}&date_from=${dateFrom}&date_to=${selectedDate}`;
      const res = await axios.get(url, config);
      setHistory(res.data);
      const todayChecklist = res.data.find((entry) => entry.date === selectedDate);
      if (todayChecklist) {
        setItems(normalizeItems(todayChecklist.items));
        setNotes(todayChecklist.notes || '');
      } else {
        setItems(buildDefaultItems());
        setNotes('');
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Gagal memuat checklist');
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchProperties();
  }, []);

  useEffect(() => {
    fetchHistory();
  }, [selectedProperty, selectedDate]);

  const toggleItem = (index) => {
    if (!canEdit) return;
    setItems((prev) => prev.map((item, idx) => idx === index ? { ...item, done: !item.done } : item));
  };

  const handleSave = async () => {
    if (!canEdit || !selectedProperty) return;
    try {
      setSaving(true);
      setError('');
      const token = localStorage.getItem('token');
      const payload = {
        property_name: selectedProperty,
        date: selectedDate,
        items,
        notes
      };
      await axios.post(`${BACKEND_URL}/api/staff/housekeeping/checklists`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSuccess('Checklist berhasil disimpan');
      fetchHistory();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Gagal menyimpan checklist');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="w-12 h-12 border-4 border-isk-teal border-t-transparent rounded-full animate-spin mx-auto" />
      </div>
    );
  }

  if (!properties.length) {
    return (
      <div className="glass-card rounded-2xl p-8 text-center">
        <p className="text-isk-muted">Belum ada properti yang bisa dikelola.</p>
      </div>
    );
  }

  return (
    <div>
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
        <div>
          <h2 className="text-xl sm:text-2xl font-bold text-isk-text">Checklist Housekeeping</h2>
          <p className="text-sm text-isk-muted mt-1">Pantau kebersihan harian untuk setiap properti</p>
        </div>
        <div className="flex flex-wrap gap-3">
          <div className="min-w-[220px]">
            <Label className="text-xs text-isk-muted">Properti</Label>
            <Select value={selectedProperty} onValueChange={setSelectedProperty}>
              <SelectTrigger className="mt-1 rounded-xl">
                <SelectValue placeholder="Pilih properti" />
              </SelectTrigger>
              <SelectContent>
                {properties.map((property) => (
                  <SelectItem key={property.name} value={property.name}>
                    {property.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs text-isk-muted">Tanggal</Label>
            <Input type="date" value={selectedDate} onChange={(e) => setSelectedDate(e.target.value)} className="mt-1 rounded-xl" />
          </div>
        </div>
      </div>

      {success && <div className="mb-4 p-3 rounded-xl bg-green-50 border border-green-200 text-green-700 text-sm">{success}</div>}
      {error && <div className="mb-4 p-3 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm">{error}</div>}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 space-y-4">
          <div className="glass-card rounded-2xl p-5">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div>
                <p className="text-sm text-isk-muted">Progress hari ini</p>
                <p className="text-2xl font-bold text-isk-text">{completion.doneCount}/{completion.total} item</p>
              </div>
              <div className="flex items-center gap-3">
                <div className="px-4 py-2 rounded-full bg-isk-teal/10 text-isk-teal font-semibold text-sm">
                  {completion.percent}% selesai
                </div>
                {refreshing && <RefreshCw className="w-4 h-4 text-isk-muted animate-spin" />}
              </div>
            </div>
          </div>

          <div className="glass-card rounded-2xl p-5">
            <div className="space-y-3">
              {items.map((item, index) => (
                <label key={`${item.label}-${index}`} className={`flex items-center gap-3 p-3 rounded-xl border ${item.done ? 'border-isk-teal/40 bg-isk-teal/5' : 'border-white/20 bg-white/40'}`}>
                  <input
                    type="checkbox"
                    checked={item.done}
                    onChange={() => toggleItem(index)}
                    className="h-4 w-4 accent-isk-teal"
                    disabled={!canEdit}
                  />
                  <span className={`text-sm ${item.done ? 'text-isk-text font-semibold' : 'text-isk-muted'}`}>{item.label}</span>
                </label>
              ))}
            </div>

            <div className="mt-4">
              <Label className="text-xs text-isk-muted">Catatan</Label>
              <Textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className="mt-1 rounded-xl"
                rows={3}
                disabled={!canEdit}
                placeholder="Catatan tambahan untuk tim..."
              />
            </div>

            {canEdit && (
              <div className="flex justify-end mt-4">
                <Button
                  onClick={handleSave}
                  disabled={saving}
                  className="bg-isk-teal hover:bg-isk-dark text-white rounded-full px-6"
                >
                  {saving ? 'Menyimpan...' : 'Simpan Checklist'}
                </Button>
              </div>
            )}
          </div>
        </div>

        <div className="space-y-4">
          <div className="glass-card rounded-2xl p-5">
            <p className="text-sm text-isk-muted mb-3">Riwayat 14 Hari Terakhir</p>
            <div className="space-y-3 max-h-[360px] overflow-y-auto">
              {history.length > 0 ? history.map((entry) => {
                const itemCount = entry.items?.length || 0;
                const doneCount = entry.items?.filter((item) => item.done).length || 0;
                const completed = itemCount > 0 && doneCount === itemCount;
                return (
                  <div key={`${entry.property_name}-${entry.date}`} className="p-3 rounded-xl bg-white/40 border border-white/20">
                    <div className="flex items-center justify-between">
                      <p className="text-sm font-semibold text-isk-text">{entry.date}</p>
                      {completed && <CheckCircle2 className="w-4 h-4 text-isk-teal" />}
                    </div>
                    <p className="text-xs text-isk-muted mt-1">{doneCount}/{itemCount} item selesai</p>
                    {entry.notes && <p className="text-xs text-isk-muted mt-2 italic">{entry.notes}</p>}
                  </div>
                );
              }) : (
                <p className="text-xs text-isk-muted">Belum ada checklist tersimpan.</p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HousekeepingChecklist;
